import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Box } from "@mantine/core";
import { notifyError, notifySuccess } from "../../../utils/notify";
import BrandedLoadingOverlay from "../../common/BrandedLoadingOverlay/BrandedLoadingOverlay";
import { useNavigate } from "react-router-dom";
import { useTemplate } from "../../../context/TemplateContext";
import { useSalon } from "../../../context/SalonContext";
import { buildTemplateRequestFromSchema } from "../utils/requestBuilder";
import {
  validateRequiredFields,
  createTabsFromSchema,
  isFieldMissing,
  generateValidationDict,
} from "../utils/validation";
import { initializeWebsiteGeneratorState } from "../utils/stateInitialization";
import {
  componentFieldToSchemaPath,
  getSchemaIdFromComponentSection as getSchemaId,
} from "../utils/fieldMapping";
import WebsiteGeneratorWizardModal from "./WebsiteGeneratorWizardModal";
import WebsiteGeneratorNavBar from "./WebsiteGeneratorNavBar";
import WebsiteGeneratorPresentator from "./WebsiteGeneratorPresentator";
import WebsiteGeneratorDrawer from "./WebsiteGeneratorDrawer";
import { BusinessInfo } from "../../../types/context";
import { generateBookingLink } from "../../../utils/bookingLinkUtils";
import {
  WebsiteGeneratorState,
  ValidationDict,
  PreviewDataMetadata,
} from "../../../types/staticWebGenerator";

const COLOR_DEFAULT_BACKGROUND = "#FFD6EE";
const COLOR_DEFAULT_TEXT = "#0000FF";
const COLOR_TEXT_BLACK = "#000000";
const COLOR_BACKGROUND_WHITE = "#FFFFFF";

interface TemplateData {
  template_content?: string;
  template_type?: string;
  template_filename?: string;
}

const WebsiteGeneratorWizard: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [template, setTemplate] = useState<TemplateData | null>(null);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);

  // Manual Preview state
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null); // Use ref for timer (no re-renders)
  const retryCountRef = useRef(0); // Use ref for retry count (no re-renders, no infinite loop)
  const previousRequestRef = useRef<any>(null); // Store previous request for comparison
  const previousHtmlRef = useRef<string | null>(null); // Store previous HTML for comparison
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Contexts
  const {
    generateManualPreview,
    generatePreview,
    loadPreviewData,
    fieldSchema,
  } = useTemplate();

  // Ref to store fieldSchema to avoid infinite loop in fetchManualPreview
  const fieldSchemaRef = useRef<typeof fieldSchema>(fieldSchema);
  const { businessInfo } = useSalon() as { businessInfo?: BusinessInfo | null };
  const navigate = useNavigate();

  // Log schema changes and update ref
  useEffect(() => {
    fieldSchemaRef.current = fieldSchema; // Keep ref in sync
    if (fieldSchema) {
    } else {
    }
  }, [fieldSchema]);

  // Track if preview data has been loaded
  const [previewDataLoaded, setPreviewDataLoaded] = useState(false);
  const [isLoadingPreviewData, setIsLoadingPreviewData] = useState(false);
  const [loadedPreviewData, setLoadedPreviewData] = useState<any>(null);
  const previewDataLoadPromiseRef = useRef<Promise<any> | null>(null);
  const justLoadedPreviewData = useRef(false); // Flag to track if preview data was just loaded

  // Publish metadata state (from loadPreviewData)
  const [publishMetadata, setPublishMetadata] =
    useState<PreviewDataMetadata | null>(null);

  // Validation state
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Unified state
  const [state, setState] = useState<WebsiteGeneratorState>(() =>
    initializeWebsiteGeneratorState(null, businessInfo),
  );

  // Validation state
  const [validationDict, setValidationDict] = useState<ValidationDict>({});

  // Local state for hero image gallery (UI-only, not in unified state)
  const [heroImageStateArray, setHeroImageStateArray] = useState<any[]>([]);

  // Track which optional sections are enabled (separate from data - controls inclusion in requests)
  // Initialize based on whether data exists (if data exists, section is enabled)
  const [optionalSectionEnabled, setOptionalSectionEnabled] = useState<
    Record<string, boolean>
  >(() => ({
    gallery: false,
    testimonials: false,
    announcementBanner: false,
    banner: false,
    highlights: false,
    team: false,
  }));

  // Sync enabled state with existing data on initial load (only once)
  const hasInitializedEnabledState = React.useRef(false);
  React.useEffect(() => {
    if (!hasInitializedEnabledState.current) {
      setOptionalSectionEnabled({
        gallery: state.gallery !== null,
        testimonials: state.testimonials !== null,
        announcementBanner: state.announcementBanner !== null,
        banner: state.banner !== null,
        highlights: state.highlights !== null,
        team: state.team !== null,
      });
      hasInitializedEnabledState.current = true;
    }
  }, [
    state.gallery,
    state.testimonials,
    state.announcementBanner,
    state.banner,
    state.highlights,
    state.team,
  ]);

  // Derived optional section states for validation (based on enabled state, not data)
  const optionalSectionStates = useMemo(
    () => ({
      gallery: optionalSectionEnabled.gallery,
      testimonials: optionalSectionEnabled.testimonials,
      announcementBanner: optionalSectionEnabled.announcementBanner,
      banner: optionalSectionEnabled.banner,
      highlights: optionalSectionEnabled.highlights,
      team: optionalSectionEnabled.team,
      showSocialIcons: state.footer?.showSocialIcons || false,
      showMap: state.contact?.showMap || false,
    }),
    [
      optionalSectionEnabled,
      state.footer?.showSocialIcons,
      state.contact?.showMap,
    ],
  );

  // Default booking link URL
  const defaultBookingLink = React.useMemo(
    () => generateBookingLink(businessInfo?.id),
    [businessInfo?.id],
  );

  // Keep booking link synced with default when not using custom link
  React.useEffect(() => {
    if (state.bookingLink && !state.bookingLink.useCustomLink) {
      setState((prev) => ({
        ...prev,
        bookingLink: prev.bookingLink
          ? { ...prev.bookingLink, link: defaultBookingLink }
          : null,
      }));
    }
  }, [defaultBookingLink, state.bookingLink?.useCustomLink]);

  // Unified state update handlers
  const updateSectionField = useCallback(
    (sectionKey: keyof WebsiteGeneratorState, field: string, value: any) => {
      setState((prev) => {
        const currentSection = prev[sectionKey];

        // If section is null, initialize it as an object
        if (currentSection === null) {
          return {
            ...prev,
            [sectionKey]: { [field]: value } as any,
          };
        }

        // Partial merge: only update the specific field
        return {
          ...prev,
          [sectionKey]: {
            ...(currentSection as any),
            [field]: value,
          },
        };
      });

      // Optimistic validation update: remove from validation dict if field was invalid
      // Only clear if validationDict has been set (after first save attempt)
      setValidationDict((prev) => {
        // Only update if validationDict has been populated (not empty)
        if (Object.keys(prev).length === 0) {
          return prev; // Don't clear if validation hasn't run yet
        }

        const sectionDict = prev[sectionKey as string];
        if (sectionDict && sectionDict[field]) {
          // Field was invalid, now has value - remove it
          const newSectionDict = { ...sectionDict };
          delete newSectionDict[field];

          // If section dict is now empty, remove the section key
          if (Object.keys(newSectionDict).length === 0) {
            const newDict = { ...prev };
            delete newDict[sectionKey as string];
            return newDict;
          }

          return {
            ...prev,
            [sectionKey as string]: newSectionDict,
          };
        }
        return prev;
      });

      // Also remove from missingFields array (only if it's been populated)
      setMissingFields((prev) => {
        // Only update if missingFields has been populated
        if (prev.length === 0) {
          return prev; // Don't clear if validation hasn't run yet
        }

        const schemaPath = componentFieldToSchemaPath(
          sectionKey as string,
          field,
        );
        if (schemaPath && prev.includes(schemaPath)) {
          return prev.filter((path) => path !== schemaPath);
        }
        return prev;
      });
    },
    [],
  );

  // Full section replace handler
  const updateSection = useCallback(
    (sectionKey: keyof WebsiteGeneratorState, data: any) => {
      setState((prev) => ({
        ...prev,
        [sectionKey]: data,
      }));

      // Clear validation for entire section
      setValidationDict((prev) => {
        const newDict = { ...prev };
        delete newDict[sectionKey as string];
        return newDict;
      });

      // Remove all schema paths for this section from missingFields
      setMissingFields((prev) => {
        const schemaId = getSchemaId(sectionKey as string);
        if (!schemaId) return prev;
        return prev.filter(
          (path) => !path.startsWith(schemaId + ".") && path !== schemaId,
        );
      });
    },
    [],
  );

  /**
   * Toggle optional section enabled state (controls inclusion in requests)
   * When disabling, removes the section data from state
   * When enabling, initializes empty structure if data doesn't exist
   */
  const toggleOptionalSection = useCallback(
    (
      sectionKey:
        | "gallery"
        | "testimonials"
        | "announcementBanner"
        | "banner"
        | "highlights"
        | "team",
      enabled: boolean,
    ) => {
      // Update the enabled state
      setOptionalSectionEnabled((prev) => ({
        ...prev,
        [sectionKey]: enabled,
      }));

      // If enabling and data doesn't exist, initialize empty structure
      if (enabled) {
        setState((prev) => {
          const currentData = prev[sectionKey];
          if (currentData !== null && currentData !== undefined) {
            // Data already exists, keep it
            return prev;
          } else {
            // No data exists, create empty structure based on section type
            const emptyData: any = {};
            if (sectionKey === "gallery") {
              emptyData.items = [];
            } else if (sectionKey === "testimonials") {
              emptyData.cards = [];
            } else if (sectionKey === "highlights") {
              emptyData.items = [];
            }
            return {
              ...prev,
              [sectionKey]: emptyData,
            };
          }
        });
      } else {
        // When disabling, remove the section data from state
        setState((prev) => {
          // Log the section data before manipulation
          const sectionDataBefore = prev[sectionKey];

          // Create a new state object and explicitly set the section to null
          // This ensures the key exists but is null, which request builders check
          const newState = { ...prev };
          newState[sectionKey] = null as any;

          // Log the section data after manipulation (should be null)
          const sectionDataAfter = newState[sectionKey];

          return newState;
        });

        // Clear validation for this section when disabling
        setValidationDict((prev) => {
          const newDict = { ...prev };
          delete newDict[sectionKey as string];
          return newDict;
        });

        // Remove from missingFields
        setMissingFields((prev) => {
          const schemaId = getSchemaId(sectionKey as string);
          if (!schemaId) return prev;
          return prev.filter(
            (path) => !path.startsWith(schemaId + ".") && path !== schemaId,
          );
        });
      }
    },
    [],
  );

  // Style handlers (for backward compatibility)
  const handleHeadingFontChange = (font: string | null) => {
    updateSectionField("fonts", "heading", font || "Jacquard 10");
  };

  const handleBodyFontChange = (font: string | null) => {
    updateSectionField("fonts", "body", font || "Arial");
  };

  const setBackgroundColor = (color: string) => {
    updateSectionField("colors", "primary", color);
  };

  const setTextColor = (color: string) => {
    updateSectionField("colors", "secondary", color);
  };

  const setSelectedTheme = (theme: string) => {
    setState((prev) => ({ ...prev, selectedTheme: theme }));
  };
  const [hasCustomDomain, setHasCustomDomain] = useState(false);

  // Load preview data when wizard first opens (non-blocking)
  useEffect(() => {
    const loadInitialPreviewData = async () => {
      if (!businessInfo?.id || previewDataLoaded || isLoadingPreviewData)
        return;

      setIsLoadingPreviewData(true);

      // Store the promise so we can await it if user exits early
      const loadPromise = loadPreviewData(businessInfo.id);
      previewDataLoadPromiseRef.current = loadPromise;

      try {
        const previewData = await loadPromise;

        // Check if we got actual data (not empty object)
        // Note: API might return empty object {} if no data exists, which is valid
        if (
          previewData &&
          typeof previewData === "object" &&
          !Array.isArray(previewData)
        ) {
          const hasData = Object.keys(previewData).length > 0;

          if (hasData) {
            setLoadedPreviewData(previewData);

            // Extract and store metadata for publish status
            if (previewData.metadata) {
              setPublishMetadata(previewData.metadata);
            } else {
              // Default metadata for unpublished state
              setPublishMetadata({
                is_published: false,
                last_published_at: null,
                last_saved_at: null,
                live_url: null,
              });
            }

            // fieldSchema is now set in TemplateContext.loadPreviewData
            // Note: LoadPreviewDataResponse may not include field_schema in the type,
            // but the API might return it, and TemplateContext handles it
            if ((previewData as any)?.field_schema) {
            } else {
            }

            // Map preview data to unified state using initialization function
            const initializedState = initializeWebsiteGeneratorState(
              previewData,
              businessInfo,
            );
            setState(initializedState);

            // Sync optional section enabled states based on loaded preview data
            // A section should be enabled if it has data in the preview
            const newOptionalSectionEnabled = {
              gallery: initializedState.gallery !== null,
              testimonials: initializedState.testimonials !== null,
              announcementBanner: initializedState.announcementBanner !== null,
              banner: initializedState.banner !== null,
              highlights: initializedState.highlights !== null,
              team: initializedState.team !== null,
            };
            setOptionalSectionEnabled(newOptionalSectionEnabled);

            // Generate initial preview using the loaded preview data immediately
            // The preview data is already in API format, so we can send it directly to populate_site_template
            // We'll generate it once state is set and schema is available
            if (businessInfo?.id) {
              // Use setTimeout to ensure state is updated first
              setTimeout(() => {
                generatePreviewFromLoadedData(previewData, businessInfo.id);
              }, 0);
            }
          } else {
          }
        } else {
          console.warn(
            "⚠️ Unexpected preview data format:",
            previewData,
            typeof previewData,
          );
        }

        setPreviewDataLoaded(true);
        // Set flag to indicate preview data was just loaded
        justLoadedPreviewData.current = true;
      } catch (error) {
        console.error("Error loading preview data:", error);
        // Log more details about the error
        if (error instanceof Error) {
          console.error("Error message:", error.message);
          console.error("Error stack:", error.stack);
        }
        // Check if it's a network/API error
        if ((error as any)?.response || (error as any)?.status) {
          console.error("API Error details:", {
            status: (error as any).status,
            response: (error as any).response,
          });
        }
        // Still set loaded to true to prevent infinite retries
        // The app will work without preview data (it will start fresh)
        setPreviewDataLoaded(true);
      } finally {
        setIsLoadingPreviewData(false);
        previewDataLoadPromiseRef.current = null;
      }
    };

    if (isModalOpen && businessInfo?.id) {
      loadInitialPreviewData();
    }
  }, [
    isModalOpen,
    businessInfo?.id,
    previewDataLoaded,
    isLoadingPreviewData,
    loadPreviewData,
  ]);

  // Wrapper functions for backward compatibility with existing components
  const handleHeroContentChange = (content: any) => {
    updateSection("hero", content);
  };

  const handleServicesContentChange = (content: any) => {
    updateSection("services", content);
  };

  const handleTeamContentChange = (content: any) => {
    updateSection("team", content);
  };

  const handleContactContentChange = (content: any) => {
    updateSection("contact", content);
  };

  const handleFooterContentChange = (content: any) => {
    updateSection("footer", content);
  };

  const handleBookingLinkContentChange = (content: any) => {
    updateSection("bookingLink", content);
  };

  // Generate preview from loaded preview data (sends directly to populate_site_template)
  const generatePreviewFromLoadedData = useCallback(
    async (loadedData: any, salonId?: string) => {
      if (!salonId || !businessInfo?.id) {
        console.warn("⚠️ Cannot generate preview: missing salon ID");
        return;
      }

      try {
        setIsPreviewLoading(true);
        setPreviewError(null);

        // The loaded preview data is already in API format
        // Just ensure we have template_type and company_name
        const requestData = {
          ...loadedData,
          template_type:
            loadedData.template_type ||
            (loadedData as any)?.template_type ||
            "generic",
          company_name:
            loadedData.company_name || businessInfo?.salon_name || "",
        };

        const response = await generateManualPreview(salonId, requestData);

        if (response?.website) {
          setPreviewHtml(response.website);
        } else {
          console.warn("⚠️ No website HTML in response");
        }
      } catch (error) {
        console.error("Error generating preview from loaded data:", error);
        // Don't set preview error - let fetchManualPreview handle it
        // This is just for the initial preview, the debounced preview will handle updates
      } finally {
        setIsPreviewLoading(false);
      }
    },
    [businessInfo, generateManualPreview],
  );

  const handleCloseModal = async () => {
    // Close modal immediately
    setIsModalOpen(false);

    // If preview data is still loading, wait for it to complete
    // This ensures drawers have data when modal closes
    if (isLoadingPreviewData && previewDataLoadPromiseRef.current) {
      try {
        await previewDataLoadPromiseRef.current;
      } catch (error) {
        console.error("Error waiting for preview data:", error);
      }
    }

    // If we have loaded preview data but no preview HTML yet, generate it now
    if (
      loadedPreviewData &&
      Object.keys(loadedPreviewData).length > 0 &&
      !previewHtml &&
      businessInfo?.id
    ) {
      generatePreviewFromLoadedData(loadedPreviewData, businessInfo.id);
    }
  };

  const handleCompleteWizard = async () => {
    // Close modal immediately
    setIsModalOpen(false);

    // If preview data is still loading, wait for it to complete
    // This ensures drawers have data when modal closes
    if (isLoadingPreviewData && previewDataLoadPromiseRef.current) {
      try {
        await previewDataLoadPromiseRef.current;
      } catch (error) {
        console.error("Error waiting for preview data:", error);
      }
    }

    // If we have loaded preview data but no preview HTML yet, generate it now
    if (
      loadedPreviewData &&
      Object.keys(loadedPreviewData).length > 0 &&
      !previewHtml &&
      businessInfo?.id
    ) {
      generatePreviewFromLoadedData(loadedPreviewData, businessInfo.id);
    }
  };

  const handleReset = () => {
    setIsModalOpen(true);
    setTemplate(null);
    setIsTemplateLoading(false);
    // Reset to initial state
    setState(initializeWebsiteGeneratorState(null, businessInfo));
    setHeroImageStateArray([]);
    setValidationDict({});
    setMissingFields([]);
    setHasCustomDomain(false);
  };

  const handleCancel = () => {
    // Navigate back to settings/website page
    navigate("/admin/settings/website");
  };

  // Handler for when publish succeeds - refresh metadata
  const handlePublishSuccess = async () => {
    if (businessInfo?.id) {
      try {
        const updatedData = await loadPreviewData(businessInfo.id);
        if (updatedData?.metadata) {
          setPublishMetadata(updatedData.metadata);
        } else {
          // After successful publish, assume published state
          setPublishMetadata({
            is_published: true,
            last_published_at: new Date().toISOString(),
            last_saved_at: publishMetadata?.last_saved_at || null,
            live_url: publishMetadata?.live_url || null,
          });
        }
      } catch (error) {
        console.error("Error refreshing metadata after publish:", error);
        // Optimistically update to published state even if refresh fails
        setPublishMetadata({
          is_published: true,
          last_published_at: new Date().toISOString(),
          last_saved_at: publishMetadata?.last_saved_at || null,
          live_url: publishMetadata?.live_url || null,
        });
      }
    }
  };

  const handleSave = async () => {
    if (!businessInfo?.id) {
      console.error("No salon ID available");
      return;
    }

    // Step 1: Get schema first (if not available, fetch it)
    let schemaToUse = fieldSchema;

    if (!schemaToUse) {
      console.warn(
        "⚠️ No fieldSchema available. Loading schema by generating preview...",
      );

      try {
        setIsPreviewLoading(true);
        // Build minimal request to get schema
        const minimalRequest = {
          template_type: state.selectedTheme || "generic",
          company_name: businessInfo.salon_name || "",
        };

        const previewResponse = await generateManualPreview(
          businessInfo.id,
          minimalRequest,
        );

        setIsPreviewLoading(false);

        if (previewResponse.field_schema) {
          schemaToUse = previewResponse.field_schema;
        } else {
          console.error("No schema in generateManualPreview response");
          setIsPreviewLoading(false);
          notifyError(
            "Failed to load website schema. Please reload the page (press F5 or Cmd+R) to continue.",
            { title: "Schema Error", autoClose: false },
          );
          return;
        }
      } catch (err) {
        setIsPreviewLoading(false);
        console.error("Error loading schema:", err);
        notifyError(
          "Failed to load website schema. Please reload the page to continue.",
          {
            title: "Schema Error",
            autoClose: false,
            onClose: () => window.location.reload(),
          },
        );
        return;
      }
    }

    // Step 2: Build the request object using schema (required)
    if (!schemaToUse) {
      notifyError(
        "Website schema is required. Please reload the page to continue.",
        {
          title: "Schema Error",
          autoClose: false,
          onClose: () => window.location.reload(),
        },
      );
      return;
    }

    let requestData;
    try {
      requestData = buildTemplateRequestFromSchema(
        state,
        businessInfo || {},
        schemaToUse,
        optionalSectionStates,
      );
    } catch (error) {
      console.error("Error building request:", error);
      notifyError(
        "Failed to build request. Please reload the page to continue.",
        {
          title: "Schema Error",
          autoClose: false,
          onClose: () => window.location.reload(),
        },
      );
      return;
    }

    // Log the request that was created

    // Step 3: Validate request object against schema
    if (schemaToUse) {
      // Build optional section states for validation (use enabled state, not data presence)
      const optionalSectionStatesForValidation: Record<string, boolean> = {
        gallery: optionalSectionEnabled.gallery,
        testimonials: optionalSectionEnabled.testimonials,
        announcementBanner: optionalSectionEnabled.announcementBanner,
        banner: optionalSectionEnabled.banner,
        highlights: optionalSectionEnabled.highlights,
        team: optionalSectionEnabled.team,
        showSocialIcons: state.footer?.showSocialIcons || false,
        showMap: state.contact?.showMap || false,
      };

      const validation = validateRequiredFields(
        requestData,
        schemaToUse,
        optionalSectionStatesForValidation,
      );

      if (!validation.isValid) {
        // Step 4: Generate validation dictionary from missing fields
        const newValidationDict = generateValidationDict(
          validation.missingFields,
          schemaToUse,
        );

        console.warn(
          "Validation failed. Missing required fields:",
          validation.missingFields,
        );

        setValidationDict(newValidationDict);
        setMissingFields(validation.missingFields);
        setShowValidationErrors(true);

        // Log after state is set (in next render)
        setTimeout(() => {}, 0);

        // Show notification to user with more details
        const missingFieldsCount = validation.missingFields.length;
        const missingValuesCount = validation.missingValues.length;
        const emptyValuesCount = validation.emptyValues.length;

        // Group missing fields by section for better error message
        const fieldsBySection: Record<string, string[]> = {};
        for (const fieldPath of validation.missingFields) {
          const section = fieldPath.split(".")[0];
          if (!fieldsBySection[section]) {
            fieldsBySection[section] = [];
          }
          fieldsBySection[section].push(fieldPath);
        }

        const sectionNames = Object.keys(fieldsBySection);
        const sectionList =
          sectionNames.length > 0
            ? ` Sections with errors: ${sectionNames.join(", ")}.`
            : "";

        notifyError(
          `Please fill out all required fields. ${missingFieldsCount} field(s) missing.${sectionList} Check the form for highlighted fields.`,
          { title: "Validation Error", autoClose: 7000 },
        );

        // IMPORTANT: Don't proceed with API call - return early
        return;
      }
    }

    // Step 5: Clear validation errors if validation passes
    setValidationDict({});
    setMissingFields([]);
    setShowValidationErrors(false);

    try {
      setIsPreviewLoading(true);

      // Call generatePreview (saves to internal/ folder)
      const response = await generatePreview(businessInfo.id, requestData);

      setIsPreviewLoading(false);

      // Show success notification
      notifySuccess("Website content saved successfully!", {
        title: "Success",
        autoClose: 3000,
      });
    } catch (error: any) {
      console.error("Error saving preview:", error);
      setIsPreviewLoading(false);

      // Show error notification to user
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save website content. Please check the console for details.";

      notifyError(errorMessage, { title: "Save Failed", autoClose: 7000 });

      // If error contains validation details, update validation state
      if (error?.response?.data?.validation_errors) {
        const validationErrors = error.response.data.validation_errors;
        console.error("Backend validation errors:", validationErrors);

        // Try to parse backend validation errors and update UI
        // This would require mapping backend error paths to component fields
        // For now, just show the error message
      }
    }
  };

  const handlePublish = () => {
    // Publish website leaving until implemented
  };

  const handleResetToDefault = () => {
    // Reset to theme defaults
    updateSectionField("fonts", "heading", "Jacquard 10");
    updateSectionField("fonts", "body", "Arial");
    updateSectionField("colors", "primary", COLOR_DEFAULT_BACKGROUND);
    updateSectionField("colors", "secondary", COLOR_DEFAULT_TEXT);
  };

  // Fetch manual preview with current state
  // NOTE: fieldSchema is accessed via ref to avoid infinite loop
  // (generateManualPreview updates fieldSchema, which would recreate this callback)
  const fetchManualPreview = useCallback(async () => {
    try {
      setIsPreviewLoading(true);
      setPreviewError(null);

      // Use schema-driven builder if schema is available, otherwise fetch it first
      // Access fieldSchema via ref to avoid dependency loop
      let currentSchema = fieldSchemaRef.current;

      // If schema is not available, fetch it first with a minimal request
      if (!currentSchema) {
        if (!businessInfo?.id) {
          setIsPreviewLoading(false);
          // Retry shortly; businessInfo loads async from context
          setTimeout(() => fetchManualPreview(), 500);
          return;
        }

        try {
          // Build minimal request to get schema
          const minimalRequest = {
            template_type: state.selectedTheme || "generic",
            company_name: businessInfo.salon_name || "",
          };
          const schemaResponse = await generateManualPreview(
            businessInfo.id,
            minimalRequest,
          );

          if (schemaResponse?.field_schema) {
            currentSchema = schemaResponse.field_schema;
            // Update ref so subsequent calls have the schema
            fieldSchemaRef.current = currentSchema;
          } else {
            console.error("No schema in response");
            notifyError(
              "Failed to load website schema. Please reload the page (press F5 or Cmd+R) to continue.",
              {
                title: "Schema Error",
                autoClose: false,
                onClose: () => window.location.reload(),
              },
            );
            setIsPreviewLoading(false);
            return;
          }
        } catch (schemaError) {
          console.error("Error fetching schema:", schemaError);
          notifyError(
            "Failed to load website schema. Please reload the page (press F5 or Cmd+R) to continue.",
            {
              title: "Schema Error",
              autoClose: false,
              onClose: () => window.location.reload(),
            },
          );
          setIsPreviewLoading(false);
          return;
        }
      }

      // Ensure we have a schema at this point
      if (!currentSchema) {
        console.error("Schema still not available after fetch attempt");
        notifyError(
          "Failed to load website schema. Please reload the page (press F5 or Cmd+R) to continue.",
          {
            title: "Schema Error",
            autoClose: false,
            onClose: () => window.location.reload(),
          },
        );
        setIsPreviewLoading(false);
        return;
      }

      let requestData;
      try {
        requestData = buildTemplateRequestFromSchema(
          state,
          businessInfo || {},
          currentSchema,
          optionalSectionStates,
        );
      } catch (error) {
        console.error("Error building request:", error);
        notifyError(
          "Failed to build request. Please reload the page (press F5 or Cmd+R) to continue.",
          {
            title: "Schema Error",
            autoClose: false,
            onClose: () => window.location.reload(),
          },
        );
        setIsPreviewLoading(false);
        return;
      }

      // Store current request for next comparison
      previousRequestRef.current = JSON.parse(JSON.stringify(requestData));

      if (!businessInfo?.id) {
        setIsPreviewLoading(false);
        setTimeout(() => fetchManualPreview(), 500);
        return;
      }
      const salonId = businessInfo.id;

      const response = await generateManualPreview(salonId, requestData);

      // Log the complete schema response for debugging
      if (response && response.field_schema) {
      }

      if (response && response.website) {
        previousHtmlRef.current = response.website;
        setPreviewHtml(response.website);
        retryCountRef.current = 0; // Reset retry count on success
      } else {
        throw new Error("No website HTML in response");
      }
    } catch (error) {
      console.error("Failed to fetch manual preview:", error);

      // Retry logic using ref (won't cause re-render or infinite loop)
      if (retryCountRef.current < 1) {
        retryCountRef.current += 1;
        setTimeout(() => fetchManualPreview(), 1000);
      } else {
        setPreviewError("Internal Error. Please try again.");
        retryCountRef.current = 0; // Reset for next attempt
      }
    } finally {
      setIsPreviewLoading(false);
    }
  }, [
    state,
    businessInfo,
    generateManualPreview,
    optionalSectionStates, // ✅ ADDED: Include optional section states to trigger updates when toggles change
    // fieldSchema intentionally omitted - accessed via ref to prevent infinite loop
    // (it gets updated by generateManualPreview, which would recreate this callback)
  ]);

  // Debounced preview update on state changes
  useEffect(() => {
    // Skip if modal is still open
    if (isModalOpen) return;

    // Skip if preview data was just loaded - the preview has already been generated
    if (justLoadedPreviewData.current) {
      justLoadedPreviewData.current = false; // Reset flag
      return;
    }

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Reset retry count when starting a new request
    retryCountRef.current = 0;

    // Show loading spinner immediately
    setIsPreviewLoading(true);

    // Set new timer (1.5 seconds)
    debounceTimerRef.current = setTimeout(() => {
      fetchManualPreview();
    }, 1500);

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [state, isModalOpen, fetchManualPreview, optionalSectionStates]); // ✅ ADDED: Include optional section states to trigger updates when toggles change

  // Extract values from unified state for backward compatibility with existing components
  // Convert null to undefined for components that expect undefined
  const selectedTheme = state.selectedTheme;
  const headingFont = state.fonts?.heading || "Jacquard 10";
  const bodyFont = state.fonts?.body || "Arial";
  const backgroundColor = state.colors?.primary || COLOR_DEFAULT_BACKGROUND;
  const textColor = state.colors?.secondary || COLOR_DEFAULT_TEXT;
  const heroContent = state.hero || undefined;
  const servicesContent = state.services || undefined;
  const teamContent = state.team || undefined;
  const contactContent = state.contact || undefined;
  const footerContent = state.footer || undefined;
  const bookingLinkContent = state.bookingLink || undefined;
  // Pass full gallery object (with title, items, gridLayout) - data is preserved even when disabled
  const galleryContent = state.gallery
    ? {
        title: state.gallery.title,
        items: state.gallery.items || [],
        gridLayout: state.gallery.gridLayout,
      }
    : undefined;
  // All optional section content is preserved even when disabled
  const testimonialsContent = state.testimonials || undefined;
  const announcementBannerContent = state.announcementBanner || undefined;
  const bannerContent = state.banner || undefined;
  const highlightsContent = state.highlights || undefined;
  const selectedHeroPhoto = state.selectedHeroPhoto || undefined;

  // Helper functions for component props
  const setSelectedHeroPhoto = (photo: any) => {
    updateSection("selectedHeroPhoto" as keyof WebsiteGeneratorState, photo);
  };

  const setGalleryContent = (
    itemsOrContent:
      | any[]
      | { items?: any[]; title?: string; gridLayout?: string },
  ) => {
    // Handle both array (legacy) and object formats
    if (Array.isArray(itemsOrContent)) {
      // Preserve existing title and gridLayout if they exist
      const currentGallery = state.gallery;
      updateSection("gallery", {
        ...currentGallery,
        items: itemsOrContent,
      });
    } else {
      // Full gallery object with title, items, gridLayout
      updateSection("gallery", itemsOrContent);
    }
  };

  const setTestimonialsContent = (content: any) => {
    updateSection("testimonials", content);
  };

  const setAnnouncementBannerContent = (content: any) => {
    updateSection("announcementBanner", content);
  };

  const setBannerContent = (content: any) => {
    updateSection("banner", content);
  };

  const setHighlightsContent = (content: any) => {
    updateSection("highlights", content);
  };

  /**
   * Handler for optional section state changes from child components
   * Delegates to toggleOptionalSection for proper state management
   */
  const setOptionalSectionStates = useCallback(
    (states: any) => {
      const sectionKeys: Array<
        | "gallery"
        | "testimonials"
        | "announcementBanner"
        | "banner"
        | "highlights"
        | "team"
      > = [
        "gallery",
        "testimonials",
        "announcementBanner",
        "banner",
        "highlights",
        "team",
      ];

      for (const key of sectionKeys) {
        if (states[key] !== undefined) {
          // toggleOptionalSection handles:
          // - Updating optionalSectionEnabled state
          // - Initializing empty data when enabling
          // - Clearing data (setting to null) when disabling
          // - Clearing validation for disabled sections
          toggleOptionalSection(key, states[key]);
        }
      }
    },
    [toggleOptionalSection],
  );

  return (
    <Box
      style={{
        height: "100dvh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <BrandedLoadingOverlay
        visible={!isModalOpen && isLoadingPreviewData}
        style={{ position: "fixed", zIndex: 2000 }}
      />
      {isModalOpen ? (
        <WebsiteGeneratorWizardModal
          onClose={handleCloseModal}
          onComplete={handleCompleteWizard}
          setTemplate={setTemplate}
          setIsTemplateLoading={setIsTemplateLoading}
          businessInfo={businessInfo}
          loadedPreviewData={loadedPreviewData}
        />
      ) : (
        <Box
          style={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          {/* Navigation Bar */}
          <WebsiteGeneratorNavBar
            onCancel={handleCancel}
            onSave={handleSave}
            onPublish={handlePublish}
            hasCustomDomain={hasCustomDomain}
            isPreviewLoading={isPreviewLoading}
            isPublished={publishMetadata?.is_published || false}
            lastSavedAt={publishMetadata?.last_saved_at || null}
            onPublishSuccess={handlePublishSuccess}
          />

          {/* Main Content Area */}
          <Box style={{ flex: 1, display: "flex", overflow: "hidden" }}>
            {/* Presentator */}
            <Box style={{ flex: 2, padding: "20px" }}>
              <WebsiteGeneratorPresentator
                selectedTheme={selectedTheme}
                headingFont={headingFont}
                bodyFont={bodyFont}
                backgroundColor={backgroundColor}
                textColor={textColor}
                heroContent={heroContent}
                servicesContent={servicesContent}
                teamContent={teamContent}
                contactContent={contactContent}
                footerContent={footerContent}
                // Optional sections
                galleryContent={
                  optionalSectionStates.gallery
                    ? galleryContent?.items || []
                    : []
                }
                testimonialsContent={
                  optionalSectionStates.testimonials &&
                  testimonialsContent?.cards
                    ? testimonialsContent.cards.map((card) => ({
                        name: card.name || "",
                        testimonial: card.text || "",
                      }))
                    : []
                }
                // Preview props
                previewHtml={previewHtml}
                isPreviewLoading={isPreviewLoading}
                previewError={previewError}
                onRetryPreview={fetchManualPreview}
              />
            </Box>

            {/* Drawer */}
            <WebsiteGeneratorDrawer
              selectedTheme={selectedTheme}
              onThemeSelect={setSelectedTheme}
              headingFont={headingFont}
              bodyFont={bodyFont}
              backgroundColor={backgroundColor}
              textColor={textColor}
              onHeadingFontChange={handleHeadingFontChange}
              onBodyFontChange={handleBodyFontChange}
              onBackgroundColorChange={setBackgroundColor}
              onTextColorChange={setTextColor}
              onResetToDefault={handleResetToDefault}
              // Content props
              heroContent={heroContent}
              onHeroContentChange={handleHeroContentChange}
              heroImageStateArray={heroImageStateArray}
              onHeroImageStateChange={setHeroImageStateArray}
              selectedHeroPhoto={selectedHeroPhoto}
              onHeroPhotoSelect={setSelectedHeroPhoto}
              servicesContent={servicesContent}
              onServicesContentChange={handleServicesContentChange}
              teamContent={teamContent}
              onTeamContentChange={handleTeamContentChange}
              contactContent={contactContent}
              onContactContentChange={handleContactContentChange}
              footerContent={footerContent}
              onFooterContentChange={handleFooterContentChange}
              // Optional content props
              galleryContent={galleryContent}
              onGalleryContentChange={setGalleryContent}
              testimonialsContent={testimonialsContent}
              onTestimonialsContentChange={setTestimonialsContent}
              announcementBannerContent={announcementBannerContent}
              onAnnouncementBannerContentChange={setAnnouncementBannerContent}
              bannerContent={bannerContent}
              onBannerContentChange={setBannerContent}
              highlightsContent={highlightsContent}
              onHighlightsContentChange={setHighlightsContent}
              optionalSectionStates={optionalSectionStates}
              onOptionalSectionStatesChange={setOptionalSectionStates}
              // Booking link props
              bookingLinkContent={bookingLinkContent}
              onBookingLinkContentChange={handleBookingLinkContentChange}
              // Schema and validation props
              fieldSchema={fieldSchema}
              missingFields={missingFields}
              validationDict={validationDict}
              // Loading state props
              isLoadingPreviewData={isLoadingPreviewData}
              isTemplateLoading={isTemplateLoading}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default WebsiteGeneratorWizard;
